// src/common/api.js
const DEFAULTS = {
  prodBase: "https://pelaikan-api-772587166095.europe-west9.run.app", // Cloud Run
  localBase: "https://127.0.0.1:5050",                                  // Dev FastAPI (run_engine.py)
  localBaseHttp: "http://127.0.0.1:5051",                              // Fallback HTTP for Office add-ins (port 5051)
  pingPath: "/__ping__",
  sessionCacheKey: "fc.apiBase.resolved",                               // per-tab cache
  probeTimeoutMs: 1000,                                                 // keep it snappy
};
// ---- Environment-aware HTTP layer ------------------------------------------
// If we're running inside Tauri, we lazily switch to the native HTTP plugin.
// Otherwise, we use the WebView/browser fetch (works for the Word add-in too).

let useNative = false;
let nativeFetch = null;

/**
 * Call once, early in your Desktop app boot (e.g., desktop index.js).
 * In the Word add-in, this is a no-op and falls back to window.fetch.
 */
export async function initHttpIfTauri() {
  try {
    // Detect Tauri v2 global
    if (typeof window !== "undefined" && (window.__TAURI__ || window.__TAURI_IPC__)) {
      const mod = await import("@tauri-apps/plugin-http");
      nativeFetch = mod.fetch;
      useNative = true;
      console.log("[api] Tauri HTTP plugin enabled");
    }
  } catch (e) {
    useNative = false;
    nativeFetch = null;
    console.warn("[api] native HTTP unavailable, falling back to window.fetch:", e);
  }
}

/** Unified fetch wrapper - exported for use in other modules. */
export async function request(url, init = {}) {
  // Auto-inject Bearer token for authenticated requests
  const isDesktopApp = typeof window !== "undefined" && window.footcheckAuth?.getAccessToken;
  const isWebApp = typeof window !== "undefined" && window.footcheckAuth?.getAccessToken &&
                   (window.location?.pathname?.includes('web-app') || document.title?.includes('Web'));
  const isWordAddin = typeof window !== "undefined" && typeof Office !== "undefined";
  const isCloudRunUrl = url.includes("pelaikan-api") || url.includes("europe-west9.run.app");
  const isLocalServerUrl = url.includes("127.0.0.1") || url.includes("localhost");
  
  // Desktop app: inject token for BOTH local server (dev mode) AND Cloud Run (prod mode) requests
  if (isDesktopApp && (isLocalServerUrl || isCloudRunUrl)) {
    try {
      const token = await window.footcheckAuth.getAccessToken();
      if (token) {
        if (!init.headers) {
          init.headers = {};
        }
        if (init.headers instanceof Headers) {
          init.headers.set("Authorization", `Bearer ${token}`);
        } else {
          init.headers["Authorization"] = `Bearer ${token}`;
        }
      }
    } catch (e) {
      console.warn("[api] Could not get access token for authenticated request:", e);
    }
  }
  
  // Web app: inject token for Cloud Run requests
  if (isWebApp && isCloudRunUrl) {
    try {
      const token = await window.footcheckAuth.getAccessToken();
      if (token) {
        if (!init.headers) {
          init.headers = {};
        }
        if (init.headers instanceof Headers) {
          init.headers.set("Authorization", `Bearer ${token}`);
        } else {
          init.headers["Authorization"] = `Bearer ${token}`;
        }
      }
    } catch (e) {
      console.warn("[api] Could not get access token for web app request:", e);
    }
  }
  
  // Inject routing headers for hosted web app calls
  if (isWebApp && isCloudRunUrl) {
    const clientId = getClientId();
    if (!init.headers) {
      init.headers = {};
    }
    if (init.headers instanceof Headers) {
      init.headers.set("X-App-Source", "web_app");
      if (clientId) init.headers.set("X-Client-Id", clientId);
    } else {
      init.headers["X-App-Source"] = "web_app";
      if (clientId) init.headers["X-Client-Id"] = clientId;
    }
  }
  
  // Word add-in: inject token for local server AND Cloud Run requests
  if (isWordAddin && (isLocalServerUrl || isCloudRunUrl)) {
    try {
      const { ensureFreshToken } = await import('../word-addin/auth/session.js');
      const token = await ensureFreshToken();
      if (token) {
        if (!init.headers) {
          init.headers = {};
        }
        if (init.headers instanceof Headers) {
          init.headers.set("Authorization", `Bearer ${token}`);
        } else {
          init.headers["Authorization"] = `Bearer ${token}`;
        }
      }
    } catch (e) {
      console.warn("[api] Could not get access token for Word add-in request:", e);
    }
  }

  const isFormData = typeof FormData !== "undefined" && init?.body instanceof FormData;

  // Always let the browser build multipart boundaries correctly
  if (isFormData) {
    // IMPORTANT: do NOT set Content-Type header yourself here.
    return fetch(url, { cache: "no-store", mode: "cors", ...init });
  }

  // JSON/text/etc: prefer plugin-http in Tauri for production and local HTTPS servers
  const isLocalDevServer = url.includes('127.0.0.1') || url.includes('localhost');
  const isLocalHttpsServer = isLocalDevServer && url.startsWith('https://');
  
  // IMPORTANT: Use Tauri HTTP plugin for:
  // 1. HTTP local requests from HTTPS pages (avoids mixed content blocking)
  // 2. HTTP local requests from tauri:// origin (WebView may block otherwise)
  // 3. HTTPS local requests (handles self-signed certificates)
  // 4. Remote HTTPS requests (production API)
  const protocol = typeof window !== 'undefined' ? window.location?.protocol : '';
  const isTauriOrigin = protocol === 'tauri:' || protocol === 'https:' || 
                        (typeof window !== 'undefined' && (window.__TAURI__ || window.__TAURI_IPC__));
  const isMixedContent = isTauriOrigin && url.startsWith('http://');
  
  // In Tauri apps, always use native HTTP plugin for local server requests
  // This bypasses WebView's mixed content restrictions on both Windows and macOS
  const shouldUseNativeForLocal = useNative && isTauriOrigin && isLocalDevServer;
  
  if (useNative && nativeFetch && (!isLocalDevServer || isMixedContent || isLocalHttpsServer || shouldUseNativeForLocal)) {
    try {
      console.log('[api] Using native Tauri HTTP plugin for:', url);
      console.log('[api] isTauriOrigin:', isTauriOrigin, 'isLocalHttpsServer:', isLocalHttpsServer, 'isMixedContent:', isMixedContent);
      // Add connectTimeout option for Tauri HTTP plugin
      // For local HTTPS servers, disable SSL verification (self-signed certs)
      const tauriInit = {
        ...init,
        connectTimeout: 30000, // 30 second timeout
      };
      
      // Disable SSL verification for localhost (self-signed certificates)
      // Requires 'dangerous-settings' feature in Cargo.toml for tauri-plugin-http
      if (isLocalHttpsServer) {
        console.log('[api] Accepting invalid certs for local HTTPS server');
        tauriInit.danger = {
          acceptInvalidCerts: true,
          acceptInvalidHostnames: true
        };
      }
      
      const response = await nativeFetch(url, tauriInit);
      console.log('[api] Native fetch succeeded for:', url);
      return response;
    } catch (error) {
      console.error('[api] Native fetch failed for:', url);
      console.error('[api] Error type:', error.constructor.name);
      console.error('[api] Error message:', error.message);
      // For mixed content scenarios, don't fall back to window.fetch - it will be blocked
      // Just re-throw and let the caller retry
      if (isMixedContent) {
        console.error('[api] Mixed content scenario - not falling back to window.fetch (would be blocked)');
        throw error;
      }
      console.error('[api] Falling back to window.fetch');
      return fetch(url, { cache: "no-store", mode: "cors", ...init });
    }
  }
  
  // Use window.fetch for local dev servers or fallback
  // For mixed content scenarios, this will fail on macOS - suppress the error
  if (isMixedContent) {
    console.warn('[api] Attempting window.fetch for mixed content (will likely fail on macOS):', url);
    try {
      return await fetch(url, { cache: "no-store", mode: "cors", ...init });
    } catch (error) {
      console.warn('[api] window.fetch failed as expected for mixed content:', error.message);
      throw error; // Re-throw so caller can retry
    }
  }
  return fetch(url, { cache: "no-store", mode: "cors", ...init });
}

// ---- Helpers ----
function parseSearch() {
  try { return new URLSearchParams(window.location.search); }
  catch { return new URLSearchParams(); }
}

async function probe(url, timeoutMs = DEFAULTS.probeTimeoutMs) {
  const ctl = new AbortController();
  const to = setTimeout(() => ctl.abort(), timeoutMs);
  console.log("[api] probing", url);
  try {
    // Use request() instead of fetch() to benefit from Tauri HTTP plugin
    const r = await request(url, { 
      method: "GET", 
      signal: ctl.signal, 
      credentials: "omit",
      mode: "cors",
      cache: "no-store"
    });
    console.log("[api] probe response:", r.status, r.statusText, r.ok);
    
    // Check if this is a session-only server (prod mode)
    if (r.ok) {
      try {
        const data = await r.json();
        return { alive: true, sessionOnly: data.session_only === true };
      } catch {
        // If not JSON, assume it's a dev server (returns "pong" string)
        return { alive: true, sessionOnly: false };
      }
    }
    return { alive: false, sessionOnly: false };
  } catch (error) {
    console.log("[api] probe error:", error.name, error.message);
    return { alive: false, sessionOnly: false };
  } finally {
    clearTimeout(to);
  }
}

// ---- Core: resolve base (and set globals for legacy code) ----
export async function resolveApiBase({
  prodBase = DEFAULTS.prodBase,
  localBase = DEFAULTS.localBase,
  localBaseHttp = DEFAULTS.localBaseHttp,
  pingPath = DEFAULTS.pingPath,
} = {}) {
  // 0) Use per-tab cache if present
  const cached = sessionStorage.getItem(DEFAULTS.sessionCacheKey);
  if (cached) {
    // ensure globals are set even when we hit cache
    window.API_BASE = cached;
    window.CLIENT_ID = window.CLIENT_ID || getClientId();
    return cached;
  }

  const qs = parseSearch();

  // 1) Hard override via ?api=...
  const qsApi = qs.get("api");
  if (qsApi) {
    console.log("qsApi", qsApi);
    sessionStorage.setItem(DEFAULTS.sessionCacheKey, qsApi);
    window.API_BASE = qsApi;
    window.CLIENT_ID = window.CLIENT_ID || getClientId();
    return qsApi;
  }

  // 2) Persistent override via localStorage
  const lsApi = localStorage.getItem("fc.apiBase.override");
  if (lsApi) {
    console.log("lsApi", lsApi);
    sessionStorage.setItem(DEFAULTS.sessionCacheKey, lsApi);
    window.API_BASE = lsApi;
    window.CLIENT_ID = window.CLIENT_ID || getClientId();
    return lsApi;
  }

  // 2.5) NEW: If we're in the desktop app or web app with authentication, use Cloud Run (unless USE_LOCAL_API is set)
  // In dev mode (USE_LOCAL_API=true), use local FastAPI for everything
  // In prod simulation (USE_LOCAL_API=false), use Cloud Run for API calls
  const isDesktopApp = typeof window !== "undefined" && window.footcheckAuth;
  const isWebApp = typeof window !== "undefined" && window.footcheckAuth && 
                   (window.location?.pathname?.includes('web-app') || 
                    document.title?.includes('Web'));
  // Account page and other Firebase-hosted pages should always use Cloud Run
  // BUT: exclude localhost for dev mode
  const isFirebaseHostedPage = typeof window !== "undefined" && 
                               !window.location?.hostname?.includes('localhost') &&
                               (window.location?.hostname?.includes('pelaikan-app') ||
                                window.location?.hostname?.includes('firebaseapp.com') ||
                                window.location?.pathname?.includes('account'));
  // Fix: process.env values are strings, not booleans
  const useLocalApi = process.env.USE_LOCAL_API === 'true' || process.env.USE_LOCAL_API === true;
  console.log("[api] Environment check - isDesktopApp:", isDesktopApp, "isWebApp:", isWebApp, "isFirebaseHostedPage:", isFirebaseHostedPage, "USE_LOCAL_API:", process.env.USE_LOCAL_API, "useLocalApi:", useLocalApi);
  
  // Web app and Firebase-hosted pages always use Cloud Run
  if (isWebApp || isFirebaseHostedPage) {
    console.log("[api] Web app or Firebase-hosted page detected, using Cloud Run for all API calls");
    sessionStorage.setItem(DEFAULTS.sessionCacheKey, prodBase);
    window.API_BASE = prodBase;
    window.CLIENT_ID = window.CLIENT_ID || getClientId();
    return prodBase;
  }  
  
  if (isDesktopApp && !useLocalApi) {
    console.log("[api] Desktop app detected (prod simulation), using Cloud Run for all API calls");
    sessionStorage.setItem(DEFAULTS.sessionCacheKey, prodBase);
    window.API_BASE = prodBase;
    window.CLIENT_ID = window.CLIENT_ID || getClientId();
    return prodBase;
  }
  
  if (isDesktopApp && useLocalApi) {
    console.log("[api] Desktop app detected (dev mode), will probe local server");
  }

  // 3) Probe local FastAPI; try multiple configurations (Word Add-In path and Desktop dev mode)
  const localConfigs = [
    { base: localBase, name: "HTTPS 5050" },
    { base: localBaseHttp, name: "HTTP 5051" },
    { base: "http://127.0.0.1:5050", name: "HTTP 5050" },
    { base: "https://127.0.0.1:5051", name: "HTTPS 5051" },
  ];
  
  let isLocalAlive = false;
  let selectedLocalBase = localBase;
  
  for (const config of localConfigs) {
    console.log(`[api] Attempting to probe local server at: ${config.base + pingPath} (${config.name})`);
    const result = await probe(config.base + pingPath);
    if (result.alive) {
      // Skip session-only servers for general API usage (Word add-in should use Cloud Run)
      if (result.sessionOnly) {
        console.log(`[api] Local server at ${config.name} is session-only, skipping for general API usage`);
        continue;
      }
      isLocalAlive = true;
      selectedLocalBase = config.base;
      console.log(`[api] Local server found at ${config.name}:`, config.base);
      break;
    } else {
      console.log(`[api] Local server not available at ${config.name}`);
    }
  }
  
  console.log("[api] Local server probe result:", isLocalAlive, "using base:", selectedLocalBase);
  const base = isLocalAlive ? selectedLocalBase : prodBase;
  console.log("[api] Selected API base:", base);

  // 4) Save & expose globals for legacy code
  console.log("base", base);
  sessionStorage.setItem(DEFAULTS.sessionCacheKey, base);
  window.API_BASE = base;
  window.CLIENT_ID = window.CLIENT_ID || getClientId();

  return base;
}

// ---- Helpers ---------------------------------------------------------------

export async function safeFetchJson(url, init = {}) {
  const res = await request(url, init);
  const text = await res.text();
  if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}\n${text.slice(0, 500)}`);
  try { return JSON.parse(text); } catch { throw new Error(`Bad JSON\n${text.slice(0, 500)}`); }
}

export function getClientId() {
  // In Tauri desktop app: use authenticated session's client_id
  // In Word add-in: use dev fallback (will get client_id from local /session endpoint)
  try {
    // Check if we're in the Tauri app and have auth module available
    if (window.__TAURI__ && window.footcheckAuth?.getClientId) {
      return window.footcheckAuth.getClientId();
    }
    
    // Fallback for Word add-in or development mode
    let clientId = localStorage.getItem("fc_client_id");
    if (!clientId) {
      // Generate a stable UUIDv4-like value (no dependency)
      const rnd = (n=16)=>crypto && crypto.getRandomValues ? crypto.getRandomValues(new Uint8Array(n)) : Array.from({length:n},()=>Math.floor(Math.random()*256));
      const bytes = rnd(16);
      // Set version and variant bits for v4
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, b => b.toString(16).padStart(2, "0"));
      clientId = `${hex.slice(0,4).join("")}-${hex.slice(4,6).join("")}-${hex.slice(6,8).join("")}-${hex.slice(8,10).join("")}-${hex.slice(10,16).join("")}`;
      localStorage.setItem("fc_client_id", clientId);
    }
    return clientId;
  } catch {
    return 'dev-session-only'; // Safe fallback
  }
}


export async function generateDocumentId(pdfBytes) {
  try {
    const hashBuffer = await crypto.subtle.digest("SHA-256", pdfBytes);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
    return `desktop_${hashHex.slice(0, 8)}`;
  } catch (error) {
    console.warn("Hash generation failed, falling back to client ID:", error);
    const clientId = getClientId();
    return `desktop_${clientId.slice(0, 8)}`;
  }
}